/* Product page: media gallery, variant selection, sticky add-to-cart, recently viewed tracking. */
(function () {
  'use strict';

  const theme = window.theme;
  const RECENT_KEY = 'theme:recently-viewed';

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.index = 0;

      this.onScroll = theme.debounce(() => this.syncFromScroll(), 60);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));
      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if (this.lightbox) {
        this.addEventListener('click', (event) => {
          const opener = event.target.closest('[data-lightbox-open]');
          if (opener && !opener.disabled) this.openLightbox(Number(opener.dataset.lightboxOpen));
        });
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.lightbox.close();
        });
        this.lightbox.addEventListener('close', () => {
          document.documentElement.classList.remove('scroll-lock');
          const opener = this.querySelector(`[data-lightbox-open="${this._lightboxIndex}"]`);
          if (opener) opener.focus({ preventScroll: true });
        });
      }

      const initial = this.track.querySelector('[data-initial]');
      if (initial) this.goTo(this.slides.indexOf(initial), true);
      this.updateUI();
    }

    goTo(index, instant) {
      if (!this.slides.length) return;
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[target];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      this.index = target;
      this.updateUI();
    }

    showMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) this.goTo(this.slides.indexOf(slide));
    }

    syncFromScroll() {
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (index !== this.index) {
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        this.updateUI();
      }
    }

    updateUI() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === this.index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          const list = thumb.closest('[data-gallery-thumbs]');
          if (list) {
            // Centre the active thumbnail inside its strip (horizontal or vertical) without scrolling the page.
            const listRect = list.getBoundingClientRect();
            const thumbRect = thumb.getBoundingClientRect();
            if (list.scrollWidth > list.clientWidth) {
              list.scrollBy({ left: thumbRect.left - listRect.left - listRect.width / 2 + thumbRect.width / 2, behavior: 'smooth' });
            }
            if (list.scrollHeight > list.clientHeight) {
              list.scrollBy({ top: thumbRect.top - listRect.top - listRect.height / 2 + thumbRect.height / 2, behavior: 'smooth' });
            }
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = String(this.index + 1);
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    }

    openLightbox(index) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this._lightboxIndex = index;
      this.lightbox.showModal();
      document.documentElement.classList.add('scroll-lock');
      const slide = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      const track = this.lightbox.querySelector('[data-lightbox-track]');
      if (slide && track) track.scrollTo({ top: slide.offsetTop, behavior: 'auto' });
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product info (ties picker, form, price, media together) ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.picker = this.querySelector('variant-picker');
      this.section = this.closest('.product-section') || this;
      this.form = document.getElementById(this.dataset.formId);
      this.gallery = this.querySelector('media-gallery');
      this.requestId = 0;

      if (this.picker) {
        this.picker.addEventListener('change', this.onOptionChange.bind(this));
      }

      this.initStickyBar();
      this.trackRecentlyViewed();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    getVariants() {
      if (!this.picker) return [];
      if (!this.picker.variants) {
        const json = this.picker.querySelector('[data-variants-json]');
        this.picker.variants = json ? JSON.parse(json.textContent) : [];
      }
      return this.picker.variants;
    }

    selectedOptions() {
      const options = [];
      this.picker.querySelectorAll('.variant-picker__option').forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select[data-option-select]');
        if (checked) options.push(checked.value);
        else if (select) options.push(select.value);
      });
      return options;
    }

    onOptionChange(event) {
      const input = event.target;
      if (input.matches('input[type="radio"]')) {
        const legendValue = input.closest('fieldset').querySelector('[data-selected-value]');
        if (legendValue) legendValue.textContent = input.value;
      }
      const options = this.selectedOptions();
      const variant = this.getVariants().find((v) => v.options.every((value, i) => value === options[i]));
      this.updateAvailability(options);
      this.updateButtons(variant);

      if (!variant) return;
      this.updateIdInputs(variant.id);
      this.updateUrl(variant.id);
      if (variant.featured_media && this.gallery) this.gallery.showMedia(variant.featured_media.id);
      this.renderVariantSections(variant.id);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability(selected) {
      const variants = this.getVariants();
      const groups = this.picker.querySelectorAll('.variant-picker__option');
      groups.forEach((group, optionIndex) => {
        const isAvailable = (value) =>
          variants.some(
            (v) =>
              v.available &&
              v.options[optionIndex] === value &&
              v.options.every((val, i) => i === optionIndex || val === selected[i])
          );
        group.querySelectorAll('input[type="radio"]').forEach((radio) => {
          const label = group.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
          if (label) label.classList.toggle('is-unavailable', !isAvailable(radio.value));
        });
        group.querySelectorAll('select option').forEach((option) => {
          const available = isAvailable(option.value);
          option.toggleAttribute('data-unavailable', !available);
          const base = option.value;
          option.textContent = available ? base : `${base} – ${theme.strings.unavailable}`;
        });
      });
    }

    updateButtons(variant) {
      const buttons = [
        ...this.section.querySelectorAll('[data-add-button]'),
        ...this.section.querySelectorAll('[data-sticky-add]'),
        ...this.section.querySelectorAll('[data-buy-now]')
      ];
      let label = theme.strings.addToCart;
      let disabled = false;
      if (!variant) {
        label = theme.strings.unavailable;
        disabled = true;
      } else if (!variant.available) {
        label = theme.strings.soldOut;
        disabled = true;
      }
      buttons.forEach((button) => {
        button.disabled = disabled;
        const span = button.querySelector('[data-add-label]');
        if (span) span.textContent = label;
      });
      const dynamic = this.section.querySelector('[data-dynamic-checkout]');
      if (dynamic) dynamic.classList.toggle('is-hidden', disabled);
      const error = this.section.querySelector('[data-main-product-form] [data-form-error]');
      if (error) error.hidden = true;
    }

    updateIdInputs(id) {
      if (!this.form) return;
      this.form.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = id;
      });
    }

    updateUrl(id) {
      if (this.closest('[data-no-url-update]')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    }

    async renderVariantSections(variantId) {
      const requestId = ++this.requestId;
      try {
        const doc = await theme.fetchSection(`${this.productUrl}?variant=${variantId}`, this.sectionId);
        if (requestId !== this.requestId) return;
        this.section.querySelectorAll('[data-variant-update]').forEach((el) => {
          const next = doc.getElementById(el.id);
          if (next) el.innerHTML = next.innerHTML;
        });
      } catch (error) {
        /* Keep current price; buttons already reflect the selected variant. */
      }
    }

    initStickyBar() {
      const bar = this.section.querySelector('[data-sticky-atc]');
      const mainButton = this.section.querySelector('[data-main-product-form] [data-add-button]');
      if (!bar || !mainButton || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', passed);
          // Lets chat widgets (e.g. WhatsApp) move up above the bar instead of covering its button.
          document.documentElement.style.setProperty('--sticky-atc-height', `px`);
          document.body.classList.toggle('sticky-atc-open', passed && bar.classList.contains('sticky-atc--chat-space') && getComputedStyle(bar).display !== 'none');
          bar.setAttribute('aria-hidden', String(!passed));
          bar.querySelectorAll('button').forEach((b) => (b.tabIndex = passed ? 0 : -1));
        },
        { threshold: 0 }
      );
      this.observer.observe(mainButton);
      bar.setAttribute('aria-hidden', 'true');
      bar.querySelectorAll('button').forEach((b) => (b.tabIndex = -1));
    }

    trackRecentlyViewed() {
      if (!theme.recentlyViewed) return;
      const handle = this.dataset.productHandle;
      if (!handle) return;
      try {
        const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
      } catch (error) {
        /* Storage unavailable (private mode) – feature silently disabled. */
      }
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
})();
